// src/i18n/index.ts
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

declare const require: {
    context?: (path: string, deep?: boolean, filter?: RegExp) => {
        keys: () => string[];
        <T>(id: string): T;
    };
};

const LANGUAGE_STORAGE_KEY = 'app_language';
const LANGUAGE_INITIALIZED_KEY = 'app_language_initialized';
const SUPPORTED_LANGUAGES = ['zh', 'en', 'ja'] as const;

const normalizeLanguage = (language?: string | null) => {
    const normalizedLanguage = language?.toLowerCase() ?? '';

    if (normalizedLanguage.startsWith('zh')) {
        return 'zh';
    }

    if (normalizedLanguage.startsWith('ja')) {
        return 'ja';
    }

    return 'en';
};

const getStoredLanguage = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

    if (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage as typeof SUPPORTED_LANGUAGES[number])) {
        return storedLanguage;
    }

    return null;
};

const getInitialLanguage = () => {
    if (typeof window === 'undefined') {
        return 'en';
    }

    const storedLanguage = getStoredLanguage();
    if (storedLanguage) {
        return storedLanguage;
    }

    const hasInitializedLanguage = window.localStorage.getItem(LANGUAGE_INITIALIZED_KEY) === 'true';
    if (!hasInitializedLanguage) {
        const detectedLanguage = normalizeLanguage(window.navigator.language);
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, detectedLanguage);
        window.localStorage.setItem(LANGUAGE_INITIALIZED_KEY, 'true');
        return detectedLanguage;
    }

    return 'en';
};

const initialLanguage = getInitialLanguage();

// 初始化资源对象
const resources: Record<string, { translation: Record<string, any> }> = {
    zh: { translation: {} },
    en: { translation: {} },
    ja: { translation: {} },
};

// 【核心】使用 Vite 的 glob 自动导入所有 .i18n.ts 文件
const loadTranslationModules = () => {
    const context = require.context!('../', true, /\.i18n\.ts$/);
    return context.keys().map((key) => context<{ default?: any }>(key)?.default).filter(Boolean);
};

for (const mod of loadTranslationModules()) {
    if (mod && mod.namespace && mod.translations) {
        // 将各个组件的翻译合并到全局
        ['zh', 'en', 'ja'].forEach((lang) => {
            if (mod.translations[lang]) {
                resources[lang].translation[mod.namespace] = mod.translations[lang];
            }
        });
    }
}

i18n
    // 使用语言检测插件（会自动把选择的语言存入 localStorage）
    .use(LanguageDetector)
    // 注入 react-i18next
    .use(initReactI18next)
    .init({
        lng: initialLanguage,
        resources,
        supportedLngs: [...SUPPORTED_LANGUAGES],
        fallbackLng: 'en',
        debug: false,
        interpolation: {
            escapeValue: false, // React 已经自带防 XSS
        },
        detection: {
            order: ['localStorage'],
            lookupLocalStorage: LANGUAGE_STORAGE_KEY,
            caches: ['localStorage'],
        }
    });

const syncDocumentLanguage = (language?: string | null) => {
    if (typeof document === 'undefined') {
        return;
    }

    document.documentElement.lang = normalizeLanguage(language);
};

syncDocumentLanguage(i18n.resolvedLanguage || i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

export default i18n;
